import type { ApiAdmininsightsordersGetCollectionParams } from '@r/api/generated/client.schemas.ts';
import { useApiAdmininsightsordersGetCollection } from '@r/api/generated/client.ts';
import { BoxesIcon, CoinsIcon, PercentIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useState, useMemo } from 'react';
import type { Option } from '@r/components/kpi/filters/select-tag-input.tsx';
import { KpiFilterBar } from '@r/components/kpi/filters/kpi-filter-bar.tsx';
import { UseWorkspace } from '@r/hooks/use-workspace.ts';
import { StatBrick } from '@admin/components/stat-brick.tsx';
import pointsTranslation from '@r/translations/points-translation.ts';
import { useUser } from '@r/api/auth.tsx';
import {
  type FormattedDataForRendering,
  BarChartBloc
} from '@r/components/kpi/points/bar-chart-bloc.tsx';
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from '@ui/card.tsx';
import { BarChartSkeleton } from '@r/components/kpi/skeleton/bar-chart-skeleton.tsx';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { companyStatuses } from '@r/types/index.ts';
import { useOrvalQueryErrorToast } from '@r/hooks/use-orval-error-toast.ts';

export default function OrdersInsights() {
  const { t } = useTranslation();
  const user = useUser().data;
  const workspaces = UseWorkspace(user);

  const [companyStatus, setCompanyStatusFilterValue] = useState<Option[]>([
    { id: 'company.status.registered', name: t('company.status.registered') }
  ]);
  const [companyTypologies, setCompanyTypologiesFilterValue] = useState<Option[]>([]);
  const [selectedSalesIds, setSelectedSalesIds] = useState<string[]>([]);
  const [dateRange, setDateRange] = useState<{ from?: string; to?: string }>({});

  const statusIds = companyStatus
    .map((status) => String(status.id))
    .filter((id): id is (typeof companyStatuses)[number] =>
      companyStatuses.includes(id as (typeof companyStatuses)[number])
    );

  const typologyNames = companyTypologies.map((typology) => String(typology.id));
  const ordersParams: ApiAdmininsightsordersGetCollectionParams = {
    ...(statusIds.length > 0 ? { 'status[]': statusIds } : {}),
    ...(typologyNames.length > 0 ? { 'typology[]': typologyNames } : {}),
    ...(selectedSalesIds.length > 0 ? { 'sales[]': selectedSalesIds } : {}),
    ...(dateRange.from ? { from: dateRange.from } : {}),
    ...(dateRange.to ? { to: dateRange.to } : {})
  };
  const {
    data: ordersInsights,
    isSuccess: isOrdersInsightsSuccess,
    isLoading: isOrdersInsightsLoading,
    isError: isOrdersInsightsError,
    error: ordersInsightsError,
    errorUpdatedAt: ordersInsightsErrorUpdatedAt
  } = useApiAdmininsightsordersGetCollection(ordersParams, {
    query: { enabled: !!workspaces.workspace?.id }
  });
  useOrvalQueryErrorToast(
    {
      isError: isOrdersInsightsError,
      error: ordersInsightsError,
      errorUpdatedAt: ordersInsightsErrorUpdatedAt
    },
    t('toast.error.label')
  );

  const totalAcquiredPoints: number = useMemo(() => {
    return Math.abs(ordersInsights?.insightPoint?.totalAcquiredPoints ?? 0);
  }, [ordersInsights?.insightPoint?.totalAcquiredPoints]);

  const percentPointsSpent: number = useMemo(() => {
    const spentAbs = Math.abs(ordersInsights?.totalSpentPoints ?? 0);
    if (totalAcquiredPoints <= 0) return 0;
    return (spentAbs / totalAcquiredPoints) * 100;
  }, [ordersInsights?.totalSpentPoints, totalAcquiredPoints]);

  const formattedOrdersPerMonth: FormattedDataForRendering[] = useMemo(() => {
    if (!ordersInsights?.totalOrdersPerMonth) return [];
    return ordersInsights.totalOrdersPerMonth.map(
      (item: { value: number; month: number; year: number }) => {
        const monthName = format(new Date(item.year, item.month - 1), 'MMMM', { locale: fr });
        const label = monthName.charAt(0).toUpperCase() + monthName.slice(1);
        const value = Number(item.value ?? 0);
        const bgColorClass = value > 0 ? 'primary' : 'muted';
        return {
          date: `${label} ${item.year}`,
          data: value,
          fill: `hsl(var(--${bgColorClass}))`
        };
      }
    );
  }, [ordersInsights]);

  const formattedPointsSpentPerMonth: FormattedDataForRendering[] = useMemo(() => {
    if (!ordersInsights?.totalPointsSpentPerMonth) return [];
    return ordersInsights.totalPointsSpentPerMonth.map(
      (item: { value: number; month: number; year: number }) => {
        const monthName = format(new Date(item.year, item.month - 1), 'MMMM', { locale: fr });
        const label = monthName.charAt(0).toUpperCase() + monthName.slice(1);
        const value = Math.abs(Number(item.value ?? 0));
        const bgColorClass = value > 0 ? 'primary' : 'muted';
        return {
          date: `${label} ${item.year}`,
          data: value,
          fill: `hsl(var(--${bgColorClass}))`
        };
      }
    );
  }, [ordersInsights]);

  return (
    <>
      <KpiFilterBar
        onStatusChange={setCompanyStatusFilterValue}
        onTypologyChange={setCompanyTypologiesFilterValue}
        companyTypologies={companyTypologies}
        onSalesChange={setSelectedSalesIds}
        initialSelectedStatuses={[
          { id: 'company.status.registered', name: t('company.status.registered') }
        ]}
        onDateRangeChange={(range) => {
          setDateRange(range);
        }}
        dateRange={dateRange}
      />

      <div className="flex w-full">
        <div className="flex flex-col gap-6 w-full">
          <div className="grid grid-cols-12 gap-6">
            <div className="flex col-span-12 xl:col-span-6">
              <StatBrick
                title={t('orders.insights.total.orders')}
                icon={<BoxesIcon />}
                value={ordersInsights?.totalOrders}
                hasValidData={isOrdersInsightsSuccess}
                loading={isOrdersInsightsLoading || isOrdersInsightsError}
              />
            </div>

            <div className="flex col-span-12 xl:col-span-3">
              <StatBrick
                title={t('orders.insights.spent.points')}
                icon={<CoinsIcon />}
                value={Math.abs(ordersInsights?.totalSpentPoints ?? 0)}
                valueSuffix={pointsTranslation(t, ordersInsights?.totalSpentPoints)}
                hasValidData={isOrdersInsightsSuccess}
                loading={isOrdersInsightsLoading || isOrdersInsightsError}
              />
            </div>

            {/* kpi pourcentage de points dépensés */}
            <div className="flex col-span-12 xl:col-span-3">
              <StatBrick
                title={t('orders.insights.spent.percentage')}
                icon={<PercentIcon />}
                value={percentPointsSpent}
                decimals={2}
                valueSuffix="%"
                hasValidData={isOrdersInsightsSuccess}
                loading={isOrdersInsightsLoading || isOrdersInsightsError}
              />
            </div>

            {/* Nombre de commandes par mois */}
            <div className="col-span-12 w-full xl:col-span-6">
              <Card className="flex h-[450px] flex-col">
                <CardHeader className="space-y-0">
                  <CardTitle className="flex items-center mb-6 space-x-2 text-xl font-author-complete font-regular">
                    <BoxesIcon />
                    <div className="text-xl font-bold">
                      {t('orders.insights.chart.commands.title')}
                    </div>
                  </CardTitle>
                </CardHeader>

                <CardContent className="flex grow">
                  {(isOrdersInsightsLoading || isOrdersInsightsError) && <BarChartSkeleton />}
                  {isOrdersInsightsSuccess && formattedOrdersPerMonth.length > 0 && (
                    <BarChartBloc formattedDataPerMonth={formattedOrdersPerMonth} />
                  )}
                </CardContent>
                <CardFooter className="justify-center text-sm font-light text-muted-foreground">
                  {t('orders.insights.chart.commands.info')}
                </CardFooter>
              </Card>
            </div>

            {/* Points dépensés par mois */}
            <div className="col-span-12 w-full xl:col-span-6">
              <Card className="flex h-[450px] flex-col">
                <CardHeader className="space-y-0">
                  <CardTitle className="flex items-center mb-6 space-x-2 text-xl font-author-complete font-regular">
                    <CoinsIcon />
                    <div className="text-xl font-bold">
                      {t('orders.insights.chart.points.title')}
                    </div>
                  </CardTitle>
                </CardHeader>
                <CardContent className="flex grow">
                  {(isOrdersInsightsLoading || isOrdersInsightsError) && <BarChartSkeleton />}
                  {isOrdersInsightsSuccess && formattedPointsSpentPerMonth.length > 0 && (
                    <BarChartBloc
                      formattedDataPerMonth={formattedPointsSpentPerMonth}
                      dataType={'point'}
                    />
                  )}
                </CardContent>
                <CardFooter className="justify-center text-sm font-light text-muted-foreground">
                  {t('orders.insights.chart.points.info')}
                </CardFooter>
              </Card>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
